import { useMemo } from "react";
import { getBuilderWarnings, readFormDefinition } from "@froppio/form-core";
import { FormSheet } from "@froppio/form-renderer";
import { Section } from "@/shared/components/ui/Section";
import { EmptyState } from "@/shared/components/ui/EmptyState";

export interface FormPreviewData {
  name: string;
  version: number;
  definition: string;
  versionId?: string;
}

export function FormPreview({ form }: { form: FormPreviewData }) {
  const result = useMemo(() => readFormDefinition(form.definition), [form.definition]);
  if (!result.success) {
    return (
      <div className="min-w-0 space-y-6">
        <div>
          <h2 className="break-words text-xl font-bold">{form.name}</h2>
          <p className="text-sm text-muted-foreground">Version {form.version}</p>
        </div>
        <EmptyState title="This form cannot be previewed" message={`${result.message} The saved definition has not been changed. Review it on the Forms website.`} />
      </div>
    );
  }
  const warnings = getBuilderWarnings(result.draft);
  return (
    <div className="min-w-0 space-y-4">
      <p className="text-center text-xs text-muted-foreground">Read-only preview. Answers and signatures can't be entered here.</p>
      {warnings.length > 0 && (
        <Section title="Complete your form before using it" card>
          <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
            {warnings.map((warning, index) => <li key={`${warning.id}-${index}`}>{warning.message}</li>)}
          </ul>
        </Section>
      )}
      <FormSheet draft={result.draft} title={form.name} version={form.version} />
    </div>
  );
}
